// src/pages/Settings/SiteSettings.jsx
// ตั้งค่าเว็บไซต์หน้า Landing: รูป Hero + ไอคอนระบบ (เฉพาะระดับภาค)
import React, { useEffect, useState } from 'react';
import { Card, Button, message, Space, Typography, Alert, Upload, Divider, Input } from 'antd';
import { UploadOutlined, SaveOutlined, PictureOutlined, SmileOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import SystemIcon, { DEFAULT_SYSTEM_ICON } from '../../components/SystemIcon';

const { Title, Text } = Typography;

const ICON_EMOJIS = ['🛕', '☸️', '🕉️', '📿', '🏯', '🪷', '🌅', '☀️'];

// ย่อขนาดรูปผ่าน canvas
function readAndResize(file, maxWidth, quality = 0.85, outputType = 'image/jpeg') {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL(outputType, quality));
      };
      img.onerror = () => reject(new Error('อ่านรูปไม่สำเร็จ'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'));
    reader.readAsDataURL(file);
  });
}

const SiteSettings = () => {
  const { settings, refresh } = useSiteSettings();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isRegionLevel = user.level === 'ภาค';

  const [heroImage, setHeroImage] = useState(null);
  const [systemIcon, setSystemIcon] = useState(null);
  const [iconText, setIconText] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingHero, setLoadingHero] = useState(false);
  const [loadingIcon, setLoadingIcon] = useState(false);

  useEffect(() => {
    setHeroImage(settings.hero_image || null);
    const icon = settings.system_icon || null;
    setSystemIcon(icon);
    if (icon && !icon.startsWith('data:') && !icon.startsWith('http')) {
      setIconText(icon);
    } else {
      setIconText('');
    }
  }, [settings]);

  const handleUploadHero = (file) => {
    setLoadingHero(true);
    readAndResize(file, 1920, 0.82, 'image/jpeg')
      .then((dataUrl) => {
        setHeroImage(dataUrl);
        message.success('เลือกรูป Hero เรียบร้อย (กดบันทึกเพื่อใช้งาน)');
      })
      .catch(() => message.error('อ่านรูป Hero ไม่สำเร็จ กรุณาเลือกไฟล์ใหม่'))
      .finally(() => setLoadingHero(false));
    return false;
  };

  const handleUploadIcon = (file) => {
    setLoadingIcon(true);
    readAndResize(file, 256, 0.9, 'image/png')
      .then((dataUrl) => {
        setSystemIcon(dataUrl);
        setIconText('');
        message.success('เลือกรูปไอคอนเรียบร้อย (กดบันทึกเพื่อใช้งาน)');
      })
      .catch(() => message.error('อ่านรูปไอคอนไม่สำเร็จ กรุณาเลือกไฟล์ใหม่'))
      .finally(() => setLoadingIcon(false));
    return false;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/site-settings`,
        { hero_image: heroImage || null, system_icon: systemIcon || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('บันทึกการตั้งค่าเว็บไซต์เรียบร้อยแล้ว');
      refresh();
    } catch (err) {
      message.error(err.response?.data?.error || 'ไม่สามารถบันทึกการตั้งค่าได้');
    } finally {
      setSaving(false);
    }
  };

  if (!isRegionLevel) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="ไม่มีสิทธิ์เข้าถึง"
          description="เฉพาะผู้ใช้ระดับภาคเท่านั้นที่สามารถตั้งค่าหน้าเว็บไซต์ (Hero / ไอคอนระบบ) ได้"
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>
        <PictureOutlined /> ตั้งค่าเว็บไซต์
      </Title>
      <Text type="secondary">จัดการรูป Hero หน้า Landing และไอคอนระบบให้เป็นเอกลักษณ์ขององค์กร</Text>

      {/* ===== Hero Image ===== */}
      <Card title="รูป Hero หน้า Landing" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
          <div
            style={{
              width: 420,
              maxWidth: '100%',
              height: 180,
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid #e0d3b4',
              background: '#f3ecdb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {heroImage ? (
              <img
                src={heroImage}
                alt="ตัวอย่าง Hero"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Text type="secondary">ยังไม่ได้ตั้งค่า ระบบใช้ภาพไล่โทนเริ่มต้น</Text>
            )}
          </div>

          <div style={{ maxWidth: 420 }}>
            <Space direction="vertical" size={12}>
              <Upload accept="image/*" showUploadList={false} beforeUpload={handleUploadHero}>
                <Button icon={<UploadOutlined />} loading={loadingHero}>เลือกรูป Hero</Button>
              </Upload>
              {heroImage && (
                <Button icon={<ReloadOutlined />} onClick={() => setHeroImage(null)}>
                  ใช้ภาพไล่โทนเริ่มต้น
                </Button>
              )}
              <Text type="secondary" style={{ fontSize: 13 }}>
                แนะนำ: ภาพกว้าง 1920px ขึ้นไป ระบบจะย่อ/บีบอัดให้อัตโนมัติ (JPG)
              </Text>
            </Space>
          </div>
        </div>
      </Card>

      {/* ===== System Icon ===== */}
      <Card title="ไอคอนระบบ" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              border: '2px solid #d4af37',
              background: 'linear-gradient(145deg, #fdf6e3, #f3e7c8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <SystemIcon icon={systemIcon} size={44} />
          </div>

          <div style={{ maxWidth: 460 }}>
            <Space direction="vertical" size={12}>
              <Upload accept="image/*" showUploadList={false} beforeUpload={handleUploadIcon}>
                <Button icon={<UploadOutlined />} loading={loadingIcon}>
                  เลือกรูปไอคอน (PNG โปร่งใส)
                </Button>
              </Upload>

              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                  หรือเลือกสัญลักษณ์สำเร็จรูป:
                </Text>
                <Space wrap size={[8, 8]}>
                  {ICON_EMOJIS.map((emoji) => (
                    <Button
                      key={emoji}
                      size="large"
                      type={systemIcon === emoji ? 'primary' : 'default'}
                      onClick={() => {
                        setSystemIcon(emoji);
                        setIconText(emoji);
                      }}
                      style={{ padding: '0 8px' }}
                    >
                      {emoji}
                    </Button>
                  ))}
                </Space>
              </div>

              <div style={{ maxWidth: 300 }}>
                <Input
                  prefix={<SmileOutlined />}
                  placeholder="หรือพิมพ์อีโมจิ/สัญลักษณ์ เช่น ☸️"
                  value={iconText}
                  maxLength={16}
                  onChange={(e) => {
                    setIconText(e.target.value);
                    setSystemIcon(e.target.value);
                  }}
                />
              </div>

              {systemIcon && systemIcon !== DEFAULT_SYSTEM_ICON && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setSystemIcon(DEFAULT_SYSTEM_ICON);
                    setIconText(DEFAULT_SYSTEM_ICON);
                  }}
                >
                  ใช้ไอคอนเริ่มต้น
                </Button>
              )}
            </Space>
          </div>
        </div>
        <Divider />
        <Text type="secondary">
          ไอคอนระบบจะแสดงที่: หัวเว็บไซต์สาธารณะ • หน้าเข้าสู่ระบบ • แถบเมนูระบบ • ตราหน้า Landing
        </Text>
      </Card>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Button
          type="primary"
          size="large"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          บันทึกการตั้งค่า
        </Button>
      </div>
    </div>
  );
};

export default SiteSettings;

